import { RappidNode, RappidNodeType } from "helpers/Constants";

export const CARD_INSET = 1.5;

/** Header controls, right-aligned: [ duplicate ][ delete ] */
const CONTROL_ICON_SIZE = 14;
const CONTROL_ICON_GAP = 8;
export const DELETE_ICON_DX = -24;
export const DUPLICATE_ICON_DX = DELETE_ICON_DX - CONTROL_ICON_GAP - CONTROL_ICON_SIZE;
/** Body drag affordance, bottom-right (padding matches card content inset). */
export const DRAG_HANDLE_BODY_DX = -(RappidNode.CardPadding + RappidNode.DragHandleWidth);
export const DRAG_HANDLE_BODY_DY = -(RappidNode.BodyPaddingBottom + RappidNode.DragHandleHeight);

/** Apply unified dark-header chrome (badge + white type label). */
export const applyNodeAccent = (node) => {
  if (!node) return;
  node.attr('header/fill', RappidNode.HeaderFill);
  node.attr('iconBadge/fill', RappidNode.IconBadgeFill);
  node.attr('label/fill', RappidNode.TypeLabelColor);
};

/**
 * Re-apply flat-card chrome after graph.fromJSON / older saved attrs.
 * Keeps error borders; resets everything else to the current visual tokens.
 */
export const refreshCampaignNodeChrome = (node) => {
  if (!node) return;
  const roiNodeType = node.attributes?.roiNodeType;
  if (!roiNodeType || roiNodeType === RappidNode.EndOfFlow) return;

  // Add-step keeps a fixed pink pill border (hover/idle must not restyle it).
  if (roiNodeType === RappidNode.AddStep) {
    applyAddStepChrome(node);
    return;
  }

  const layout = getNodeLayout();

  // Keep delay cards on the same footprint as other action steps.
  if (roiNodeType === RappidNodeType.Delay) {
    const { width: w, height: h } = node.size?.() || {};
    if (w !== RappidNode.DelayRectWidth || h !== RappidNode.DelayRectHeight) {
      node.resize(RappidNode.DelayRectWidth, RappidNode.DelayRectHeight);
    }
  }

  const width = node.size?.()?.width;
  applyNodeAccent(node);

  if (width) {
    node.attr('header/d', pathRoundedTop(width));
  }
  node.attr('headerDivider/y', RappidNode.HeaderHeight);
  node.attr('headerDivider/fill', RappidNode.HeaderDividerColor);

  node.attr('iconBadge/width', RappidNode.IconBadgeSize);
  node.attr('iconBadge/height', RappidNode.IconBadgeSize);
  node.attr('iconBadge/x', RappidNode.IconBadgeX);
  node.attr('iconBadge/y', RappidNode.IconBadgeY);
  node.attr('iconBadge/rx', RappidNode.IconBadgeRadius);
  node.attr('iconBadge/ry', RappidNode.IconBadgeRadius);

  node.attr('typeIcon/width', RappidNode.IconSize);
  node.attr('typeIcon/height', RappidNode.IconSize);
  node.attr('typeIcon/x', layout.iconX);
  node.attr('typeIcon/y', layout.iconY);

  node.attr('label/x', layout.labelX);
  node.attr('label/y', layout.typeY);

  // Reposition drag handle into the body (clears older header y from saved graphs).
  if (node.attr('dragHandle/width') != null || node.attr('dragHandle/xlinkHref')) {
    node.removeAttr('dragHandle/y');
    node.attr('dragHandle/ref-dx', DRAG_HANDLE_BODY_DX);
    node.attr('dragHandle/ref-dy', DRAG_HANDLE_BODY_DY);
    node.attr('dragHandle/width', RappidNode.DragHandleWidth);
    node.attr('dragHandle/height', RappidNode.DragHandleHeight);
  }

  if (node.attr('body/stroke') !== RappidNode.TemplateErrorColor) {
    applyNodeIdleStyle(node);
  } else {
    node.attr('body/filter', RappidNode.DropShadow);
  }
};

/** Fixed pink pill for the Add step control. */
const applyAddStepChrome = (node) => {
  node.attr('body/stroke', RappidNode.PrimaryColor);
  node.attr('body/strokeWidth', 2);
  node.attr('body/fill', RappidNode.WhiteColor);
  node.attr('label/fill', RappidNode.PrimaryColor);
};

/** Selected card: primary border + soft glow (joint-demos node-glow-selected). */
export const applyNodeSelectedStyle = (node) => {
  if (!node || node.attributes?.roiNodeType === RappidNode.EndOfFlow) return;
  if (node.attributes?.roiNodeType === RappidNode.AddStep) {
    applyAddStepChrome(node);
    return;
  }
  node.attr('body/stroke', RappidNode.PrimaryColor);
  node.attr('body/strokeWidth', RappidNode.ActiveElementBorderStrokeWidth);
  node.attr('body/filter', RappidNode.SelectedGlow);
};

/** Rest / idle card chrome. Preserves template-error borders. */
export const applyNodeIdleStyle = (node) => {
  if (!node || node.attributes?.roiNodeType === RappidNode.EndOfFlow) return;
  if (node.attributes?.roiNodeType === RappidNode.AddStep) {
    applyAddStepChrome(node);
    return;
  }
  if (node.attr('body/stroke') === RappidNode.TemplateErrorColor) {
    node.attr('body/strokeWidth', RappidNode.ErrorElementBorderStrokeWidth);
    node.attr('body/filter', RappidNode.DropShadow);
    return;
  }
  node.attr('body/stroke', RappidNode.CardBorderColor);
  node.attr('body/strokeWidth', RappidNode.InactiveElementBorderStrokeWidth);
  node.attr('body/filter', RappidNode.DropShadow);
};

/** Hover affordance — slightly stronger border without stealing selection glow. */
export const applyNodeHoverStyle = (node) => {
  if (!node || node.attributes?.roiNodeType === RappidNode.EndOfFlow) return;
  if (node.attributes?.roiNodeType === RappidNode.AddStep) {
    applyAddStepChrome(node);
    return;
  }
  if (node.attr('body/stroke') === RappidNode.TemplateErrorColor) return;
  node.attr('body/strokeWidth', RappidNode.ActiveElementBorderStrokeWidth);
};

export const getNodeLayout = () => {
  const labelX = RappidNode.IconBadgeX + RappidNode.IconBadgeSize + RappidNode.IconTextGap;
  const iconX = RappidNode.IconBadgeX + (RappidNode.IconBadgeSize - RappidNode.IconSize) / 2;
  const iconY = RappidNode.IconBadgeY + (RappidNode.IconBadgeSize - RappidNode.IconSize) / 2;
  const headerRowCenterY = RappidNode.IconBadgeY + RappidNode.IconBadgeSize / 2;
  const bodyTextX = RappidNode.CardPadding;
  const titleY = RappidNode.HeaderHeight + RappidNode.BodyPaddingY;
  return {
    labelX,
    textX: labelX,
    bodyTextX,
    iconX,
    iconY,
    typeY: headerRowCenterY,
    titleY,
    deleteY: headerRowCenterY - CONTROL_ICON_SIZE / 2,
  };
};

/** Path with only top corners rounded — sits flush under the card stroke. */
export const pathRoundedTop = (width, height = RappidNode.HeaderHeight, radius = RappidNode.BorderRadius) => {
  const x = CARD_INSET;
  const y = CARD_INSET;
  const w = Math.max(0, width - CARD_INSET * 2);
  const h = Math.max(0, height - CARD_INSET);
  const r = Math.max(0, Math.min(radius - CARD_INSET, w / 2, h));
  return [
    `M ${x + r} ${y}`,
    `H ${x + w - r}`,
    `Q ${x + w} ${y} ${x + w} ${y + r}`,
    `V ${y + h}`,
    `H ${x}`,
    `V ${y + r}`,
    `Q ${x} ${y} ${x + r} ${y}`,
    'Z',
  ].join(' ');
};

/** Body text width — full card width minus left/right padding. */
export const getBodyTextWidth = (nodeWidth = RappidNode.RectWidth) =>
  Math.max(40, nodeWidth - RappidNode.CardPadding * 2);

/** Header type label width: leave room for control icons. */
export const getHeaderLabelWidth = (nodeWidth = RappidNode.RectWidth) =>
  Math.max(
    40,
    nodeWidth
      - RappidNode.IconBadgeX
      - RappidNode.IconBadgeSize
      - RappidNode.IconTextGap
      - RappidNode.DeleteIconReserve
  );

export const cardBodyAttrs = () => ({
  refWidth: '100%',
  refHeight: '100%',
  fill: RappidNode.WhiteColor,
  strokeWidth: RappidNode.InactiveElementBorderStrokeWidth,
  stroke: RappidNode.CardBorderColor,
  rx: RappidNode.BorderRadius,
  ry: RappidNode.BorderRadius,
  filter: RappidNode.DropShadow,
});

export const headerBandAttrs = (nodeWidth = RappidNode.RectWidth, fill = RappidNode.HeaderFill) => ({
  d: pathRoundedTop(nodeWidth),
  fill,
  strokeWidth: 0,
});

export const headerDividerAttrs = () => ({
  x: CARD_INSET,
  y: RappidNode.HeaderHeight,
  refWidth: -CARD_INSET * 2,
  height: 1,
  fill: RappidNode.HeaderDividerColor,
  strokeWidth: 0,
});

export const iconBadgeAttrs = (fill = RappidNode.IconBadgeFill) => ({
  width: RappidNode.IconBadgeSize,
  height: RappidNode.IconBadgeSize,
  x: RappidNode.IconBadgeX,
  y: RappidNode.IconBadgeY,
  fill,
  strokeWidth: 0,
  stroke: RappidNode.IconBadgeStroke,
  rx: RappidNode.IconBadgeRadius,
  ry: RappidNode.IconBadgeRadius,
});

export const typeLabelAttrs = (layout, extra = {}) => ({
  fill: RappidNode.TypeLabelColor,
  fillOpacity: RappidNode.TypeLabelOpacity,
  fontSize: RappidNode.TypeLabelFontSize,
  textVerticalAnchor: 'middle',
  fontWeight: '700',
  textAnchor: 'left',
  letterSpacing: '0.4',
  x: layout.labelX ?? layout.textX,
  y: layout.typeY,
  ...extra,
});

/** Primary body detail line — left-aligned under the header */
export const titleAttrs = (layout, extra = {}) => ({
  fill: RappidNode.DarkColor,
  fontSize: RappidNode.TitleFontSize,
  textVerticalAnchor: 'top',
  fontWeight: '600',
  textAnchor: 'left',
  x: layout.bodyTextX ?? RappidNode.CardPadding,
  y: layout.titleY,
  ...extra,
});

/** Secondary / meta body lines */
export const metaAttrs = (layout, y, extra = {}) => ({
  fill: RappidNode.MetaTextColor || RappidNode.PrimaryColor,
  fontSize: RappidNode.DescriptionFontSize,
  textVerticalAnchor: 'top',
  textAnchor: 'left',
  x: layout.bodyTextX ?? RappidNode.CardPadding,
  y,
  ...extra,
});

export const withoutY = ({ y, ...rest }) => rest;

export const deleteIconAttrs = (layout, extra = {}) => ({
  width: CONTROL_ICON_SIZE,
  height: CONTROL_ICON_SIZE,
  'ref-dx': DELETE_ICON_DX,
  y: layout.deleteY,
  cursor: 'pointer',
  ...extra,
});

export const duplicateIconAttrs = (layout, extra = {}) => ({
  width: CONTROL_ICON_SIZE,
  height: CONTROL_ICON_SIZE,
  'ref-dx': DUPLICATE_ICON_DX,
  y: layout.deleteY,
  cursor: 'pointer',
  ...extra,
});

/** Drag affordance in the body, bottom-right — same-branch reorder grip. */
export const dragHandleAttrs = (_layout, extra = {}) => ({
  width: RappidNode.DragHandleWidth,
  height: RappidNode.DragHandleHeight,
  'ref-dx': DRAG_HANDLE_BODY_DX,
  'ref-dy': DRAG_HANDLE_BODY_DY,
  cursor: 'grab',
  ...extra,
});

export const additionalDescriptionAttrs = (extra = {}) => ({
  text: '',
  fill: RappidNode.PrimaryColor,
  fontSize: RappidNode.DescriptionFontSize,
  fontWeight: 'bold',
  textVerticalAnchor: 'middle',
  textAnchor: 'middle',
  refX: '50%',
  refY: '90%',
  event: 'element:additionalDescription:pointerdown',
  ...extra,
});
